import { c as _c } from "react/compiler-runtime";
/**
 * Outpost Metrics API Data Fetching Hook
 *
 * Fetches metrics data from the Outpost Metrics API (proxied through core server).
 * Unlike the EG useMetricsQuery, this uses outpost-specific param format:
 * - time[start]/time[end] instead of date_range[start]/date_range[end]
 * - Outpost events dimensions: tenant_id, topic, destination_id (no status)
 * - Outpost attempt dimensions: tenant_id, destination_id, topic, status, code, etc.
 */

import { useContext } from 'react';
import useSWR from 'swr';
import { stringifyQuery } from '../../utils';
import { GlobalContext } from '../contexts/GlobalContext';
export type OutpostMetricsEndpoint = 'events' | 'attempts';
export interface OutpostMetricsQueryParams {
  endpoint: OutpostMetricsEndpoint;
  time: {
    start: string; // ISO 8601
    end: string; // ISO 8601
  };
  granularity?: string; // e.g. '1h', '1d'
  measures: string[]; // e.g. ['count'], ['count', 'error_rate']
  dimensions?: string[]; // e.g. ['tenant_id', 'topic']
  filters?: Record<string, string | string[]>;
}
interface OutpostMetricsDataPoint {
  time_bucket: string | null;
  dimensions: Record<string, string>;
  metrics: Record<string, number>;
}
interface OutpostMetricsResponse {
  data: OutpostMetricsDataPoint[];
  metadata: {
    granularity: string | null;
    query_time_ms: number;
    row_count: number;
    row_limit: number;
    truncated: boolean;
  };
}

/**
 * Transform outpost metrics response to the flat row format expected by chart components.
 * Maps to the same shape as EG useMetricsQuery: { "Model.field": value }
 */
function transformOutpostResponse(data: OutpostMetricsDataPoint[], model: string): Record<string, any>[] {
  return data.map(row => {
    const result: Record<string, any> = {};
    if (row.time_bucket) {
      result[`${model}.createdAt`] = row.time_bucket;
    }
    Object.entries(row.metrics).forEach(([key, value]) => {
      result[`${model}.${key}`] = String(value);
    });
    Object.entries(row.dimensions).forEach(([key, value]) => {
      result[`${model}.${key}`] = value;
    });
    return result;
  });
}
export const useOutpostMetricsQuery = query => {
  const $ = _c(16);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let request;
  let t0;
  if ($[0] !== query) {
    let t1;
    bb0: {
      if (!query) {
        t1 = null;
        break bb0;
      }
      const params = {
        time: {
          start: query.time.start,
          end: query.time.end
        },
        measures: query.measures
      };
      if (query.granularity) {
        params.granularity = query.granularity;
      }
      if (query.dimensions && query.dimensions.length > 0) {
        params.dimensions = query.dimensions;
      }
      if (query.filters && Object.keys(query.filters).length > 0) {
        params.filters = query.filters;
      }
      t1 = {
        path: `/metrics/${query.endpoint}`,
        params
      };
    }
    request = t1;
    t0 = request ? `${request.path}?${stringifyQuery(request.params)}` : null;
    $[0] = query;
    $[1] = request;
    $[2] = t0;
  } else {
    request = $[1];
    t0 = $[2];
  }
  const api_url = t0;
  let t1;
  if ($[3] !== HookdeckAPI || $[4] !== request) {
    t1 = async () => {
      if (!request) {
        throw new Error("Missing outpost metrics request");
      }
      return HookdeckAPI.outpostRequest("GET", request.path, request.params);
    };
    $[3] = HookdeckAPI;
    $[4] = request;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    data: api_response,
    mutate,
    error,
    isLoading
  } = useSWR(api_url ? `outpost-metrics-${api_url}-${HookdeckAPI.team_id}` : null, t1);
  let t2;
  bb1: {
    if (!api_response || !query) {
      t2 = null;
      break bb1;
    }
    const model = query.endpoint === "events" ? "Events" : "Attempts";
    let t3;
    if ($[6] !== api_response.data) {
      t3 = api_response.data || [];
      $[6] = api_response.data;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== model || $[9] !== t3) {
      t4 = transformOutpostResponse(t3, model);
      $[8] = model;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t2 = t4;
  }
  const raw_data = t2;
  const t3 = error ?? null;
  let t4;
  if ($[11] !== isLoading || $[12] !== mutate || $[13] !== raw_data || $[14] !== t3) {
    t4 = {
      is_loading: isLoading,
      raw_data,
      refetch: mutate,
      error: t3
    };
    $[11] = isLoading;
    $[12] = mutate;
    $[13] = raw_data;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
export default useOutpostMetricsQuery;